import Http from '../../../../network/Http'
import {ContentType} from '../../../../network/Constants'
import {NatGetResidentUserToken} from './NatGetResidentUserToken'
import {NatGetResidentUserAuthenticationKey} from './NatGetResidentUserAuthenticationKey'
import {NatPostResidentUserConfirm} from './NatPostResidentUserConfirm'
import {NatPostResidentUser} from './NatPostResidentUser'
import {NatGetResidentUserAuthed} from './NatGetResidentUserAuthed'
import {NatGetSmoothEAvailable} from './NatGetSmoothEAvailable'
import {NatGetSmileagePoint} from './NatGetSmileagePoint'
import {NatCreateGenerateRequestParam} from './NatCreateGenerateRequestParam';
import {NatCreateAuthorizeUrl} from './NatCreateAuthorizeUrl';
import {Nullable} from '../../../../app/TypeHelpers';
import {NatGetServerSessionInfo} from '../../session/repository/NatGetServerSessionInfo';
import {ApiResponse} from '../../../../network/response/ApiResponse';
import {NatSetServerSessionInfo} from './NatSetServerSessionInfo';
import {NatCreateLogoutUrl} from './NatCreateLogoutUrl';
import {NatGetResidentUserInvolvingSub} from './NatGetResidentUserInvolvingSub'

// 認証済居住者ユーザ取得
export interface ResidentUserAuthedRequest {
    mailAddress: string,
    password: string
    authenticationKey: string,
    token: string
}

// 居住者ユーザ登録確認
export interface ResidentUserConfirmRequest {
    familyName: string,
    firstName: string,
    familyNameKana: string,
    firstNameKana: string,
    birthdayYear: string,
    birthdayMonth: string,
    birthdayDay: string,
    sex: string,
    password: string,
    wishToHasekoGroupInfos: string,
    sutekiNetOfNotice?: string,
}

// 居住者ユーザ登録
export interface RegisterResidentUserRequest {
    token: string,
    authenticationKey: string
    policyAgreeType: string
    wishToHasekoGroupInfos: string,
    sutekiNetOfNotice?: string,
    referenceId?: string,
    receivingAgencyCd?: string,
    isOwnershipTransfer?: boolean
}

// 居住者ユーザ更新
export interface UpdateResidentUserRequest {
    token: string,
    authenticationKey: string
    policyAgreeType: string
    wishToHasekoGroupInfos: string,
}

// 居住者ユーザ更新（ログイン済）
export interface UpdateResidentUserLoginedRequest {
    homeMileageLinkGuideDisplay?: string,
    accessControlDiv?: string
    wishToHasekoGroupInfos?: string,
    familyName?: string,
    firstName?: string,
    familyNameKana?: string
    firstNameKana?: string
    sutekiNetOfNotice?: string
    bulletinBoardCommentNotice?: string
    billingAmountFixNotice?: string
    version: number;
}

// 認証URL作成
export interface CreateAuthorizeRequest {
    mailAddress: string | null
}

// IDトークン解析情報取得
export interface GetUserInfoByToken {
    code: string,
    state: string | null
}

// 居住者ユーザ更新（トークン）
export interface UpdateResidentUserTokenRequest {
    token: string,
}

// サーバー側のセッションに格納
export interface SetSeverSessionInfoRequest {
    authenticationKey: string | null,
    ssoLoginCondominiumCd: string | null,
    ssoLoginRidgeCd: string | null,
    ssoLoginRoomNumber: string | null,
    loginPage: string | null,
    token: string | null,
    isSutekiNetSmartPhoneApplication: string | null
}

export default class NatResidentUserRepo {
    http: Http;

    constructor(http: Http) {
        this.http = http
    }

    // 居住者ユーザ取得（ログイン済）
    async getResidentUserLogined(): Promise<NatGetResidentUserAuthed> {
        return await this.http.get(`/api/resident/residentUser/authed`);

    }

    // 居住者ユーザ取得(サブユーザ含む)
    async getResidentUserInvolvingSub(
        condominiumCd: string,
        ridgeCd: string,
        roomNumber: string
    ): Promise<NatGetResidentUserInvolvingSub> {
        return await this.http
            .get(`/api/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users`);
    }

    // 認証済居住者ユーザ取得
    async getResidentUserAuthed(requestParam: ResidentUserAuthedRequest):
        Promise<NatGetResidentUserAuthenticationKey> {
        return await this.http.post(
            '/api/resident/residentUser/idpass',
            {
                mailAddress: requestParam.mailAddress,
                password: requestParam.password,
                authenticationKey: requestParam.authenticationKey,
                token: requestParam.token,
            },
            ContentType.JSON,
        );
    }

    // 居住者ユーザ登録確認
    async residentUserConfirm(requestParam: ResidentUserConfirmRequest): Promise<NatPostResidentUserConfirm> {
        return await this.http.post(
            '/api/resident/residentUser/confirm',
            {
                familyName: requestParam.familyName,
                firstName: requestParam.firstName,
                familyNameKana: requestParam.familyNameKana,
                firstNameKana: requestParam.firstNameKana,
                birthdayYear: requestParam.birthdayYear,
                birthdayMonth: requestParam.birthdayMonth,
                birthdayDay: requestParam.birthdayDay,
                sex: requestParam.sex,
                password: requestParam.password,
                wishToHasekoGroupInfos: requestParam.wishToHasekoGroupInfos,
                sutekiNetOfNotice: requestParam.sutekiNetOfNotice,
            },
            ContentType.JSON,
        );
    }

    // 居住者ユーザ登録
    async registerResidentUser(requestParam: RegisterResidentUserRequest): Promise<NatPostResidentUser> {
        return await this.http.post(
            '/api/resident/residentUser',
            {
                token: requestParam.token,
                authenticationKey: requestParam.authenticationKey,
                policyAgreeType: requestParam.policyAgreeType,
                wishToHasekoGroupInfos: requestParam.wishToHasekoGroupInfos,
                sutekiNetOfNotice: requestParam.sutekiNetOfNotice,
                receivingAgencyCd: requestParam.receivingAgencyCd,
                isOwnershipTransfer: requestParam.isOwnershipTransfer
            },
            ContentType.JSON,
        );
    }

    // 居住者ユーザ更新
    async updateResidentUser(requestParam: UpdateResidentUserRequest): Promise<void> {
        return await this.http.put('/api/resident/residentUser', requestParam)
    }

    // 居住者ユーザ更新（ログイン済）
    async updateResidentUserLogined(requestParam: UpdateResidentUserLoginedRequest): Promise<void> {
        return await this.http.put(`/api/resident/residentUser/authed`, requestParam)
    }

    // 居住者ユーザ取得(トークン)
    async getResidentUserToken(token: string): Promise<NatGetResidentUserToken> {
        return await this.http.get(`/api/resident/residentUser/token/${token}`);
    }

    // 居住者ユーザ更新（トークン）
    async updateResidentUserToken(requestParam: UpdateResidentUserTokenRequest): Promise<void> {
        return await this.http.put(`/api/resident/residentUser/token`, requestParam)
    }

    // smooth-e利用可否取得
    async getSmoothEAvailable(): Promise<NatGetSmoothEAvailable> {
        return await this.http.get(`/api/resident/residentUser/smooth-e/availability`);
    }

    // 住まいレージポイント取得
    async getSmileagePoint(): Promise<NatGetSmileagePoint> {
        return await this.http.get(`/api/resident/residentUser/smileage-point`);
    }

    // 認可コードリクエストパラメータ生成
    async createGenerateRequestParam(): Promise<NatCreateGenerateRequestParam> {
        return await this.http.get(`/api/common/memberDataPlatform/authRequestParam`);
    }

    // 認可コードリクエストURL生成処理を行う
    async createAuthorize(requestParam: CreateAuthorizeRequest):
        Promise<NatCreateAuthorizeUrl> {
        return await this.http.post(
            '/api/common/memberDataPlatform/authorize',
            {
                mailAddress: requestParam.mailAddress
            },
            ContentType.JSON,
        );
    }

    async getBackendSessionInfo(): Promise<Nullable<NatGetServerSessionInfo>> {
        const response: ApiResponse<NatGetServerSessionInfo> =
            await this.http.get(`/api/resident/memberDataPlatform/getBackendSessionInfo`)
        return response.details
    }

    // セッション情報をサーバに格納
    async setBackendSessionInfo(requestParam: SetSeverSessionInfoRequest): Promise<NatSetServerSessionInfo> {
        return await this.http.post(
            '/api/resident/memberDataPlatform/setBackendSessionInfo',
            {
                authenticationKey: requestParam.authenticationKey,
                ssoLoginCondominiumCd: requestParam.ssoLoginCondominiumCd,
                ssoLoginRidgeCd: requestParam.ssoLoginRidgeCd,
                ssoLoginRoomNumber: requestParam.ssoLoginRoomNumber,
                loginPage: requestParam.loginPage,
                token: requestParam.token,
                isSutekiNetSmartPhoneApplication: requestParam.isSutekiNetSmartPhoneApplication
            },
            ContentType.JSON,
        );
    }

    // ログアウト要求リクエストURL生成処理を行う
    async createLogout():
        Promise<NatCreateLogoutUrl> {
        return await this.http.get(
            '/api/common/memberDataPlatform/logoutUrl');
    }
}
